if (!customElements.get('fs-recommended-products')) {
  class FsRecommendedProducts extends HTMLElement {
    constructor() {
      super();
      this.productId = this.dataset.productId || this.dataset.productID || '';
      this.recommendationLimit = Number(this.dataset.recNumber || 10);
      this.widgetsIds = this.parseWidgetsIds((this.dataset.widgetsIds || this.dataset.widgetsIDS || '').trim());
      this.withAttributes = this.dataset.withAttributes === 'true';
      this.activeWidgetId = '';
      this.delegatedListenersBound = false;
    }

    connectedCallback() {
      if (!this.productId) {
        this.closest('.shopify-section')?.classList.add('hidden');
        this.closest('.product-recommended-products')?.classList.add('hidden');
        return;
      }

      this.pollForFastSimonSdk()
        .then(() => {
          this.init();
        })
        .catch(() => {
          this.closest('.shopify-section')?.classList.add('hidden');
        });
    }

    parseWidgetsIds(widgetsIdsRaw) {
      if (!widgetsIdsRaw) return [];

      if (widgetsIdsRaw.startsWith('[')) {
        try {
          const parsed = JSON.parse(widgetsIdsRaw);
          return Array.isArray(parsed) ? parsed.map(String).filter(Boolean) : [];
        } catch (e) {
          return [];
        }
      }

      return widgetsIdsRaw
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean);
    }

    pollForFastSimonSdk() {
      const pollIntervalMs = 150;
      const pollTimeoutMs = 10000;

      return new Promise((resolve, reject) => {
        const startTimestampMs = Date.now();

        const pollTick = () => {
          const fastSimonSdkAvailable =
            typeof window !== 'undefined' &&
            window.FastSimonSDK &&
            typeof window.FastSimonSDK.productRecommendationByWidget === 'function';

          if (fastSimonSdkAvailable) {
            resolve(true);
            return;
          }

          const timedOut = Date.now() - startTimestampMs > pollTimeoutMs;
          if (timedOut) {
            reject(new Error('FastSimonSDK not available'));
            return;
          }

          setTimeout(pollTick, pollIntervalMs);
        };

        pollTick();
      });
    }

    init() {
      const requestPayload = {
        productID: String(this.productId),
        withAttributes: this.withAttributes,
        callback: (response) => this.handleRecommendationResponse(response)
      };

      if (this.widgetsIds.length) {
        requestPayload.widgetsIDS = this.widgetsIds;
      }

      try {
        window.FastSimonSDK.productRecommendationByWidget(requestPayload);
      } catch (error) {
        this.closest('.shopify-section')?.classList.add('hidden');
      }
    }

    handleRecommendationResponse(response) {
      const responsePayload = response?.payload || [];

      if (!Array.isArray(responsePayload) || !responsePayload.length) {
        this.closest('.shopify-section')?.classList.add('hidden');
        return;
      }

      const widgetResponseWithProducts = responsePayload.find(
        (widgetItem) => Array.isArray(widgetItem?.payload) && widgetItem.payload.length
      );

      if (!widgetResponseWithProducts) {
        this.closest('.shopify-section')?.classList.add('hidden');
        return;
      }

      const widgetId = String(widgetResponseWithProducts.widgetID || '');
      const recommendedProducts = widgetResponseWithProducts.payload || [];

      if (!recommendedProducts.length) {
        this.closest('.shopify-section')?.classList.add('hidden');
        return;
      }

      this.activeWidgetId = widgetId;

      this.renderResults(recommendedProducts);
      this.reportProductsShown(recommendedProducts, widgetId);
      this.addDelegatedEventListeners();
    }

    renderResults(recommendedProducts) {
      const productsToRender = recommendedProducts.slice(0, this.recommendationLimit);

      this.innerHTML = `
        <div class="relative z-[1]">
          <swiper-carousel 
            data-space-between="8"
            data-slides-per-view="4"
            data-slides-per-view-mobile="2.5"
            class="hidden md:flex flex-col w-full pb-8"
            data-pagination="true"
            data-external-nav-buttons="recommended-pdp-slider"
          >
            <button data-prev="recommended-pdp-slider" class="button-slider disabled:hidden absolute left-0 top-1/2 z-10 bg-secondaryLight border-primaryLight transform -translate-x-1/2 shadow-lg">
              <svg class="transform -scale-x-100" xmlns="http://www.w3.org/2000/svg" width="10" height="18" viewBox="0 0 10 18" fill="none">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M1.60926 0L0 1.72611L6.78148 9L0 16.2739L1.60926 18L10 9L1.60926 0Z" fill="black"/>
              </svg>
            </button>
            <button data-next="recommended-pdp-slider" class="button-slider disabled:hidden absolute right-0 top-1/2 z-10 bg-secondaryLight border-primaryLight shadow-lg transform translate-x-1/2">
              <svg xmlns="http://www.w3.org/2000/svg" width="10" height="18" viewBox="0 0 10 18" fill="none">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M1.60926 0L0 1.72611L6.78148 9L0 16.2739L1.60926 18L10 9L1.60926 0Z" fill="black"/>
              </svg>
            </button>
            <div class="swiper-wrapper">
              ${productsToRender.map((productDetail, index) => {
                const productUrl = String(productDetail?.u || '');
                const recommendedProductId = String(productDetail?.id || '');
                const recommendedPosition = index + 1;

                return `
                  <div class="swiper-slide [&_product-tile]:h-full !h-auto" data-fs-rec-product-id="${recommendedProductId}" data-fs-rec-position="${recommendedPosition}">
                    <intersection-loader 
                      data-url="${productUrl}"
                      data-section-id="recently-viewed-product-tile"
                      data-load-immediately="true"
                    ></intersection-loader>
                  </div>
                `;
              }).join('')}
            </div>
            <div class="flex items-center justify-center gap-2 pt-6 [&_.swiper-pagination-bullet]:w-2 [&_.swiper-pagination-bullet]:h-2 [&_.swiper-pagination-bullet]:rounded-full [&_.swiper-pagination-bullet]:bg-secondaryLight [&_.swiper-pagination-bullet]:border-none [&_.swiper-pagination-bullet]:opacity-100 [&_.swiper-pagination-bullet.swiper-pagination-bullet-active]:bg-primaryDark" data-pagination></div>
          </swiper-carousel>
          <div class="grid grid-cols-2 gap-4 md:hidden">
            ${productsToRender.map((productDetail, index) => {
              const productUrl = String(productDetail?.u || '');
              const recommendedProductId = String(productDetail?.id || '');
              const recommendedPosition = index + 1;

              return `
                <div class="[&_product-tile]:h-full !h-auto" data-fs-rec-product-id="${recommendedProductId}" data-fs-rec-position="${recommendedPosition}">
                  <intersection-loader 
                    data-url="${productUrl}"
                    data-section-id="recently-viewed-product-tile"
                    data-load-immediately="true"
                  ></intersection-loader>
                </div>
              `;
            }).join('')}
          </div>
        </div>
      `;
    }

    reportProductsShown(recommendedProducts, widgetId) {
      if (!window.FastSimonSDK || !window.FastSimonEventName) return;
      if (!widgetId) return;

      try {
        window.FastSimonSDK.event({
          eventName: window.FastSimonEventName.RecommendationsProductsShown,
          data: {
            products: recommendedProducts,
            widget_id: String(widgetId),
            sourceProductID: String(this.productId)
          }
        });
      } catch (e) {}
    }

    addDelegatedEventListeners() {
      if (this.delegatedListenersBound) return;
      this.delegatedListenersBound = true;

      this.addEventListener('click', (event) => {
        const clickedElement = event.target;
        if (!(clickedElement instanceof Element)) return;

        const trackingContainer = clickedElement.closest('[data-fs-rec-product-id][data-fs-rec-position]');
        if (!trackingContainer) return;

        const clickedAnchor = clickedElement.closest('a[href]');
        if (!clickedAnchor) return;

        const recommendedProductId = String(trackingContainer.getAttribute('data-fs-rec-product-id') || '');
        const recommendedPositionRaw = String(trackingContainer.getAttribute('data-fs-rec-position') || '');
        const recommendedPosition = Number(recommendedPositionRaw);

        if (!recommendedProductId || !Number.isFinite(recommendedPosition) || recommendedPosition <= 0) return;
        if (!this.activeWidgetId) return;
        if (!window.FastSimonSDK || !window.FastSimonEventName) return;

        event.preventDefault();

        try {
          window.FastSimonSDK.event({
            eventName: window.FastSimonEventName.RecommendationProductClicked,
            data: {
              productID: recommendedProductId,
              position: recommendedPosition,
              sourceProductID: String(this.productId),
              widget_id: String(this.activeWidgetId)
            }
          });
        } catch (e) {}

        window.location.href = clickedAnchor.href;
      });
    }
  }

  customElements.define('fs-recommended-products', FsRecommendedProducts);
}